import { useEffect, useMemo, useRef, useState } from 'react';
import './MerchantFeedback.css';

/* ---------- Content (edit freely) ---------- */
const CURRENT_ORDERS = ['None yet', 'Under 10', '10–25', '25–50', '50–100', '100+'];
const TICKET = ['Under $15', '$15–25', '$25–40', '$40–60', '$60+'];
const PAINS = [
  'High commission',
  'Slow payouts',
  'Unfair refunds & chargebacks',
  'Hidden fees',
  'Hard to reach support',
  'Late or careless drivers',
  'Low visibility unless I pay for ads',
  'No access to my customers',
];
const FEATURES = [
  { label: 'Next-day meals', desc: "Customers pre-order tomorrow's meals from you" },
  { label: 'Free samples', desc: 'Customers try your items free and rate them' },
  { label: 'SavePass rewards', desc: 'Reward SavePass customers who order from you' },
  { label: 'Window Share', desc: 'Earn a share when your viewers order elsewhere' },
  { label: 'Subscriber earnings', desc: 'Earn from customers who subscribe to your kitchen' },
  { label: 'Subscriber top pick', desc: "Show up first in your subscribers' app" },
  { label: 'Scan to subscribe', desc: 'Customers scan your counter QR to follow you' },
];
const YES = 'Yes';
const NO = 'Not yet';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function readParam(...keys) {
  if (typeof window === 'undefined') return '';
  const p = new URLSearchParams(window.location.search);
  for (const k of keys) if (p.get(k)) return p.get(k).trim();
  return '';
}

const optLabel = (o) => (typeof o === 'string' ? o : o.label);
const emptyDraft = {
  value: null,
  picked: [],
  otherOn: false,
  other: '',
  noteOn: false,
  note: '',
  text: '',
  signedUp: null,
  email: null,
};
const getDraft = (drafts, id) => ({ ...emptyDraft, ...(drafts[id] || {}) });
const multiValue = (d) => [...d.picked, ...(d.otherOn && d.other.trim() ? [d.other.trim()] : [])];

function buildSteps(drafts) {
  const pains = multiValue(getDraft(drafts, 'painPoints'));
  const steps = [
    {
      id: 'currentOrders',
      type: 'single',
      q: 'How many orders a day do you get from delivery apps?',
      hint: 'All apps combined, on a typical day.',
      options: CURRENT_ORDERS,
    },
    {
      id: 'avgTicket',
      type: 'single',
      q: "What's your average order total?",
      hint: 'The usual amount a customer spends per order.',
      options: TICKET,
    },
    {
      id: 'painPoints',
      type: 'multi',
      q: 'What frustrates you most on delivery apps?',
      hint: 'Pick all that apply.',
      options: PAINS,
    },
  ];
  if (pains.length !== 1) {
    steps.push({
      id: 'biggestCost',
      type: 'single',
      q: 'Which one costs you the most money?',
      hint: 'From what you picked.',
      options: pains,
    });
  }
  steps.push(
    {
      id: 'features',
      type: 'multi',
      q: 'Which SingleCombo feature do you like most?',
      hint: 'Pick all that interest you.',
      options: FEATURES,
    },
    {
      id: 'story',
      type: 'final',
      q: 'Tell us about a time a delivery app let you down',
      hint: 'Optional, but real stories help us build the right fix.',
    }
  );
  return steps;
}

function isValid(step, d, email) {
  if (step.type === 'single')
    return Boolean(d.value) && step.options.map(optLabel).includes(d.value);
  if (step.type === 'multi') return multiValue(d).length > 0;
  if (step.type === 'final') return Boolean(d.signedUp) && EMAIL_RE.test(email);
  return true;
}

const defaultSubmit = async (payload) => console.log('Merchant feedback', payload);

/* ---------- Main ---------- */
export default function MerchantFeedback({
  kitchenName = '',
  email = '',
  onSubmit,
  signupUrl = '#',
}) {
  const [urlKitchen] = useState(() => readParam('kitchen', 'kitchenName'));
  const [urlEmail] = useState(() => readParam('email'));
  const kitchen = (kitchenName || urlKitchen).trim();
  const prefilledEmail = (email || urlEmail).trim();

  const [drafts, setDrafts] = useState({});
  const [idx, setIdx] = useState(0);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const headingRef = useRef(null);

  const steps = useMemo(() => buildSteps(drafts), [drafts]);
  const step = steps[Math.min(idx, steps.length - 1)];
  const draft = getDraft(drafts, step.id);
  const final = getDraft(drafts, 'story');
  const emailValue = (final.email ?? prefilledEmail).trim();
  const finished = status !== 'idle';

  const update = (patch) =>
    setDrafts((all) => ({ ...all, [step.id]: { ...getDraft(all, step.id), ...patch } }));

  useEffect(() => {
    window.scrollTo?.({ top: 0 });
    headingRef.current?.focus({ preventScroll: true });
  }, [idx, status]);

  const buildPayload = () => {
    const d = (id) => getDraft(drafts, id);
    const questionNotes = {};
    ['currentOrders', 'avgTicket', 'painPoints', 'biggestCost', 'features'].forEach((id) => {
      const n = d(id).noteOn ? d(id).note.trim() : '';
      if (n) questionNotes[id] = n;
    });
    const pains = multiValue(d('painPoints'));
    return {
      email: emailValue.toLowerCase(),
      emailPrefilled: Boolean(prefilledEmail) && emailValue === prefilledEmail,
      kitchenName: kitchen || null,
      source: kitchen || prefilledEmail ? 'feedback_link' : 'direct',
      signedUp: final.signedUp === YES,
      currentOrdersPerDay: d('currentOrders').value,
      avgTicket: d('avgTicket').value,
      painPoints: pains,
      biggestCost: pains.length === 1 ? pains[0] : d('biggestCost').value,
      featuresWanted: multiValue(d('features')),
      story: final.text.trim(),
      questionNotes,
      submittedAt: new Date().toISOString(),
    };
  };

  const send = async () => {
    setStatus('sending');
    try {
      await (onSubmit || defaultSubmit)(buildPayload());
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  const next = () => (idx + 1 < steps.length ? setIdx(idx + 1) : send());
  const back = () => setIdx(Math.max(0, idx - 1));
  const isLast = idx >= steps.length - 1;
  const valid = isValid(step, draft, emailValue);
  const selectedCount = step.type === 'multi' ? multiValue(draft).length : 0;

  let cta = isLast ? 'Send feedback' : 'Continue';
  if (selectedCount) cta += ` · ${selectedCount} selected`;
  const shown = finished ? steps.length : Math.min(idx, steps.length - 1) + 1;

  return (
    <div className='mf'>
      <aside className='mf-side'>
        <section className={`mf-intro${idx > 0 || finished ? ' is-compact' : ''}`}>
          {kitchen && <p className='mf-for'>For {kitchen}</p>}
          <h1>Tell us what to build next</h1>
          <p className='mf-lead'>
            Tell us what frustrates you on delivery apps, and we'll build the fix. Takes about 2
            minutes.
          </p>
          <div className='mf-prog'>
            <div
              className='mf-track'
              role='progressbar'
              aria-valuemin={0}
              aria-valuemax={steps.length}
              aria-valuenow={shown}
            >
              <i style={{ width: `${(shown / steps.length) * 100}%` }} />
            </div>
            <span>{finished ? 'Done' : `${shown} of ${steps.length}`}</span>
          </div>
        </section>
      </aside>

      <main className='mf-panel'>
        {finished ? (
          <div className='mf-body'>
            {status === 'sending' && (
              <h2 className='mf-q' tabIndex={-1} ref={headingRef}>
                Sending your feedback…
              </h2>
            )}
            {status === 'sent' && (
              <>
                <h2 className='mf-q' tabIndex={-1} ref={headingRef}>
                  Thanks{kitchen ? `, ${kitchen}` : ''}!
                </h2>
                <p className='mf-hint'>
                  Your answers go straight to our product team and help decide what we build next.
                </p>
                {/* {final.signedUp !== YES && (
                  <a className='mf-btn mf-inline' href={signupUrl}>
                    Sign up your kitchen
                  </a>
                )} */}
              </>
            )}
            {status === 'error' && (
              <>
                <h2 className='mf-q' tabIndex={-1} ref={headingRef}>
                  Your feedback didn't send
                </h2>
                <p className='mf-hint'>
                  Check your connection and try again. Your answers are still here.
                </p>
                <button className='mf-btn mf-inline' onClick={send}>
                  Try again
                </button>
                <button className='mf-back mf-inline' onClick={() => setStatus('idle')}>
                  Review answers
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className='mf-body'>
              <h2 className='mf-q' tabIndex={-1} ref={headingRef}>
                {step.q}
              </h2>
              {step.hint && <p className='mf-hint'>{step.hint}</p>}

              {step.type === 'final' ? (
                <FinalStep draft={draft} update={update} emailValue={emailValue} />
              ) : (
                <>
                  <Options step={step} draft={draft} update={update} />
                  <NoteToggle draft={draft} update={update} />
                </>
              )}
            </div>

            <div className='mf-bar'>
              {idx > 0 && (
                <button className='mf-back' onClick={back}>
                  Back
                </button>
              )}
              <button className='mf-btn' disabled={!valid} onClick={next}>
                {cta}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

/* ---------- Pieces ---------- */
function Options({ step, draft, update }) {
  const multi = step.type === 'multi';
  const toggle = (o) =>
    update({
      picked: draft.picked.includes(o) ? draft.picked.filter((x) => x !== o) : [...draft.picked, o],
    });

  return (
    <div className='mf-opts' role={multi ? 'group' : 'radiogroup'} aria-label={step.q}>
      {step.options.map((opt) => {
        const o = optLabel(opt);
        const on = multi ? draft.picked.includes(o) : draft.value === o;
        return (
          <button
            key={o}
            role={multi ? 'checkbox' : 'radio'}
            aria-checked={on}
            className={`mf-opt${on ? ' is-on' : ''}`}
            onClick={() => (multi ? toggle(o) : update({ value: o }))}
          >
            <span className={multi ? 'mf-box' : 'mf-radio'} aria-hidden='true' />
            <span>
              {o}
              {opt.desc && <span className='mf-optdesc'>{opt.desc}</span>}
            </span>
          </button>
        );
      })}
      {multi && (
        <>
          <button
            role='checkbox'
            aria-checked={draft.otherOn}
            className={`mf-opt${draft.otherOn ? ' is-on' : ''}`}
            onClick={() => update({ otherOn: !draft.otherOn })}
          >
            <span className='mf-box' aria-hidden='true' />
            Something else
          </button>
          {draft.otherOn && (
            <input
              className='mf-input'
              autoFocus
              placeholder='Tell us what it is'
              aria-label='Something else'
              value={draft.other}
              onChange={(e) => update({ other: e.target.value })}
            />
          )}
        </>
      )}
    </div>
  );
}

function NoteToggle({ draft, update }) {
  if (!draft.noteOn) {
    return (
      <button className='mf-addnote' onClick={() => update({ noteOn: true })}>
        + Add a note
      </button>
    );
  }
  return (
    <div className='mf-note'>
      <label className='mf-label' htmlFor='mf-note'>
        Your note
      </label>
      <textarea
        id='mf-note'
        className='mf-input'
        rows={3}
        autoFocus
        placeholder='Anything you want to add about this question'
        value={draft.note}
        onChange={(e) => update({ note: e.target.value })}
      />
      <button className='mf-addnote' onClick={() => update({ noteOn: false, note: '' })}>
        Remove note
      </button>
    </div>
  );
}

function FinalStep({ draft, update, emailValue }) {
  const [touched, setTouched] = useState(false);
  const bad = touched && emailValue !== '' && !EMAIL_RE.test(emailValue);

  return (
    <>
      <textarea
        className='mf-input mf-big'
        rows={5}
        aria-label='Your story'
        placeholder='What happened, and what should have happened instead?'
        value={draft.text}
        onChange={(e) => update({ text: e.target.value })}
      />

      <div className='mf-extra'>
        <p className='mf-label' id='mf-su'>
          Have you signed up with SingleCombo?
        </p>
        <div className='mf-seg' role='radiogroup' aria-labelledby='mf-su'>
          {[YES, NO].map((o) => (
            <button
              key={o}
              role='radio'
              aria-checked={draft.signedUp === o}
              className={`mf-segbtn${draft.signedUp === o ? ' is-on' : ''}`}
              onClick={() => update({ signedUp: o })}
            >
              {o}
            </button>
          ))}
        </div>

        <label className='mf-label mf-gap' htmlFor='mf-email'>
          {draft.signedUp === YES ? 'Email you signed up with' : 'Your email'}
        </label>
        <input
          id='mf-email'
          type='email'
          inputMode='email'
          autoComplete='email'
          className={`mf-input${bad ? ' is-invalid' : ''}`}
          placeholder='you@yourkitchen.com'
          value={emailValue}
          aria-invalid={bad}
          aria-describedby={bad ? 'mf-email-err' : undefined}
          onChange={(e) => update({ email: e.target.value })}
          onBlur={() => setTouched(true)}
        />
        {bad && (
          <p className='mf-error' id='mf-email-err'>
            Enter a valid email, like name@kitchen.com
          </p>
        )}
      </div>
    </>
  );
}
